import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import yellow from "@mui/material/colors/yellow";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useState } from "react";
import { Map } from "schemas/functions";

import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import TemplateBody from "features/Influencer/Templates/SharedComponents/TemplateBody";
import NoMergeTagsWarningDialog from "features/Influencer/Templates/TemplatesTable/NoMergeTagsWarningDialog";
import { fetcherAuth } from "utils/api";
import { containsMergeTagsTemplates } from "utils/string";
import { makeDeepCopy } from "utils/updateLocalState";

interface Props {
  handleBack: () => void;
  handleNext: () => void;
  handleSkip?: () => void;
  onboardingForm: Map;
  setOnboardingForm: React.Dispatch<React.SetStateAction<Map>>;
}

const EditTemplateFromScratch = ({
  handleBack,
  handleNext,
  handleSkip,
}: Props) => {
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const {
    fetchOutreachTemplates,
    outreachTemplates,
    followupTemplates,
    setTemplates,
  } = useContext(OutreachTemplatesContext);
  const [openMergeTagWarning, setOpenMergeTagWarning] = useState(false);

  const [editTemplate, setEditTemplate] = useState<Template>({
    subject: "",
    body: "",
    contentType: TemplateContentType.OUTREACH,
    name: "My Outreach",
  });
  const [followupTemplate, setFollowupTemplate] = useState<Template>({
    subject: "",
    body: "",
    contentType: TemplateContentType.FOLLOWUP,
    name: "My Follow-Up",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);

  const createTemplate = async () => {
    if (!currentOrg?.id) return;
    if (
      !openMergeTagWarning &&
      !containsMergeTagsTemplates(editTemplate?.body || "")
    ) {
      setOpenMergeTagWarning(true);
      return;
    }
    try {
      setCreateLoading(true);
      const res = await fetcherAuth(
        getToken,
        editTemplate?.id
          ? `/api/organization/${currentOrg?.id}/templates/${editTemplate?.id}`
          : `/api/organization/${currentOrg?.id}/templates`,
        editTemplate?.id ? "PUT" : "POST",
        {},
        { ...editTemplate, fromOnboarding: true },
      );
      setEditTemplate(res.template);
      _updateLocalTemplate(res.template);
      setAlert("Template Saved", "success");
      if (showFollowUp && followupTemplate?.body) {
        await createFollowUp();
      }
      if (handleNext) {
        handleNext();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const createFollowUp = async () => {
    try {
      setCreateLoading(true);
      const res = await fetcherAuth(
        getToken,
        editTemplate?.id
          ? `/api/organization/${currentOrg?.id}/templates/${followupTemplate?.id}`
          : `/api/organization/${currentOrg?.id}/templates`,
        editTemplate?.id ? "PUT" : "POST",
        {},
        { ...followupTemplate, fromOnboarding: true },
      );
      setFollowupTemplate(res.template);
      _updateLocalTemplate(res.template);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const _updateLocalTemplate = (template: Template) => {
    setTemplates((prevTemplates) => {
      const updatedTemplates = makeDeepCopy(prevTemplates);
      if (template.id) {
        const index = updatedTemplates.findIndex(
          (t: Template) => t.id === template.id,
        );
        if (index !== -1) {
          updatedTemplates[index] = template;
        }
        return updatedTemplates;
      }
    });
  };

  const initializeTemplates = () => {
    if (outreachTemplates?.length === 0) {
      fetchOutreachTemplates();
    } else {
      const existingTemplate = outreachTemplates?.[0];
      if (existingTemplate) {
        setEditTemplate(existingTemplate);
      }

      if (followupTemplates && followupTemplates?.length > 0) {
        const existingFollowUp = followupTemplates[0];
        if (existingFollowUp && !followupTemplate?.id) {
          setFollowupTemplate(existingFollowUp);
        }
        setShowFollowUp(true);
      }
    }
  };

  useEffect(() => {
    initializeTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outreachTemplates?.length, followupTemplates?.length]);

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center" }}>
        <Box
          component="i"
          color="secondary.dark"
          sx={{ pr: 2 }}
          className="fa-solid fa-marker"
        />
        Paste Your Template Here
      </Typography>
      <ul>
        <li style={{ listStyleType: "none" }}>
          <Typography>
            <Box
              component="i"
              className="fa-solid fa-sparkles"
              sx={{ pr: 1, color: yellow[700] }}
            />
            Keep {"{Brand Name}"} and {"{First Name}"} as placeholders—this
            ensures each email is personalized for the brand and recipient.
          </Typography>
        </li>
        <li style={{ listStyleType: "none" }}>
          <Typography>
            <Box
              component="i"
              className="fa-solid fa-file-pen"
              sx={{ pr: 1, color: "primary.main" }}
            />
            You can always modify or create new templates later from inside the
            dashboard on the Templates page.
          </Typography>
        </li>
      </ul>

      <Box
        display="flex"
        justifyContent="center"
        alignItems={"center"}
        flexDirection="column"
      >
        <Box sx={{ width: 600 }}>
          <TemplateBody
            editTemplate={editTemplate}
            setEditTemplate={setEditTemplate}
            isNew={!(outreachTemplates?.length > 0)}
            allowChangeTitle={false}
            minimalist
            displayError={true}
          />
        </Box>
        {!showFollowUp && (
          <Button
            size="small"
            sx={{ textTransform: "none" }}
            onClick={() => setShowFollowUp(true)}
          >
            {" "}
            Add Follow-Up
          </Button>
        )}

        {showFollowUp && (
          <Box sx={{ width: 600, mt: 2 }}>
            <Grid container justifyContent={"space-between"}>
              <FormHelperText>Follow-up (optional):</FormHelperText>
              <Tooltip title="Remove follow-up">
                <IconButton
                  sx={{ fontSize: 12 }}
                  onClick={() => setShowFollowUp(false)}
                >
                  <Box component="i" className="fa-regular fa-trash" />
                </IconButton>
              </Tooltip>
            </Grid>

            <TemplateBody
              editTemplate={followupTemplate}
              setEditTemplate={setFollowupTemplate}
              isNew={true}
              allowChangeTitle={false}
              minimalist
              displayError={true}
            />
          </Box>
        )}
      </Box>

      <Grid item xs="auto" container columnGap={1}>
        <Grid item xs="auto">
          <Button onClick={handleBack} color="secondary" disableElevation>
            Back
          </Button>
        </Grid>
        <Grid item xs></Grid>

        {handleSkip && (
          <Grid item xs="auto">
            <Button onClick={handleSkip} disableElevation color="secondary">
              Skip
            </Button>
          </Grid>
        )}

        <Grid item xs="auto">
          <LoadingButton
            disableElevation
            variant="contained"
            type="submit"
            disabled={!editTemplate.subject || !editTemplate.body}
            onClick={createTemplate}
            loading={createLoading}
          >
            Next
          </LoadingButton>
        </Grid>
      </Grid>
      <NoMergeTagsWarningDialog
        open={openMergeTagWarning}
        handleClose={() => setOpenMergeTagWarning(false)}
        onProceed={createTemplate}
      />
    </>
  );
};

export default EditTemplateFromScratch;
